import React, { useMemo, useState } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import ClockLoader from 'shared/components/ClockLoader';
import TenantBrandingColorField, {
  normalizeBrandingColorForApi,
} from 'shared/components/TenantBrandingColorField';
import TenantLogoUploadField from 'shared/components/TenantLogoUploadField';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';

const STATUS_OPTIONS = ['ACTIVE', 'DISABLED', 'DELETED', 'PENDING'];

const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required'),
  url: yup.string().trim().required('JDBC URL is required'),
  username: yup.string().trim().required('Username is required'),
  driverClassName: yup.string().trim().required('Driver class is required'),
  status: yup.string().required('Status is required'),
  primaryColor: yup.string().trim().max(64),
  secondaryColor: yup.string().trim().max(64),
  backgroundColor: yup.string().trim().max(64),
  tenantPhoto: yup.string().trim().max(2048),
});

const EditTenantDialog = ({ open, onClose, onSuccess, tenant, updateTenant }) => {
  const [submitting, setSubmitting] = useState(false);

  const initialValues = useMemo(() => {
    if (!tenant) return null;
    return {
      name: tenant.name ?? '',
      url: tenant.url ?? '',
      username: tenant.username ?? '',
      password: '',
      driverClassName: tenant.driverClassName ?? 'org.postgresql.Driver',
      initialize: Boolean(tenant.initialize),
      status:
        typeof tenant.status === 'string'
          ? tenant.status
          : tenant.status?.name ?? 'ACTIVE',
      primaryColor: tenant.primaryColor ?? '',
      secondaryColor: tenant.secondaryColor ?? '',
      backgroundColor: tenant.backgroundColor ?? '',
      tenantPhoto: tenant.tenantPhoto ?? '',
    };
  }, [tenant]);

  if (!open || !tenant || !initialValues) {
    return null;
  }

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitting(true);
    try {
      const trimOrEmpty = (v) => (v == null ? '' : String(v).trim());
      const payload = {
        name: values.name.trim(),
        url: values.url.trim(),
        username: values.username.trim(),
        driverClassName: values.driverClassName.trim(),
        initialize: Boolean(values.initialize),
        status: values.status,
        primaryColor: normalizeBrandingColorForApi(values.primaryColor),
        secondaryColor: normalizeBrandingColorForApi(values.secondaryColor),
        backgroundColor: normalizeBrandingColorForApi(values.backgroundColor),
        tenantPhoto: trimOrEmpty(values.tenantPhoto),
      };
      if (values.password && values.password.trim() !== '') {
        payload.password = values.password;
      }
      await updateTenant(tenant.id, payload);
      toast.success('Tenant updated');
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      const err =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to update tenant';
      toast.error(typeof err === 'string' ? err : 'Failed to update tenant');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit tenant</DialogTitle>
      <Formik
        key={tenant.id}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <DialogContent sx={{ position: 'relative', pt: 1 }}>
              {submitting && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(255,255,255,0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <ClockLoader inline size={22} />
                </Box>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                <TextField
                  fullWidth
                  margin="dense"
                  name="name"
                  label="Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  name="username"
                  label="Username"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.username && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  name="url"
                  label="JDBC URL"
                  value={values.url}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.url && Boolean(errors.url)}
                  helperText={touched.url && errors.url}
                  sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  name="password"
                  label="New password (optional)"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText="Leave empty to keep the current password"
                />
                <TextField
                  fullWidth
                  margin="dense"
                  name="driverClassName"
                  label="Driver class name"
                  value={values.driverClassName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.driverClassName && Boolean(errors.driverClassName)}
                  helperText={touched.driverClassName && errors.driverClassName}
                />
                <FormControl fullWidth margin="dense" sx={{ minWidth: 0 }}>
                  <InputLabel id="edit-tenant-status-label">Status</InputLabel>
                  <Select
                    labelId="edit-tenant-status-label"
                    name="status"
                    label="Status"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.status && Boolean(errors.status)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 56,
                    pt: { xs: 0, sm: 1 },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.initialize}
                        onChange={(_, v) => setFieldValue('initialize', v)}
                        name="initialize"
                      />
                    }
                    label="Initialize datasource"
                  />
                </Box>

                <Divider sx={{ gridColumn: '1 / -1', my: 1 }} />
                <Typography variant="subtitle2" sx={{ gridColumn: '1 / -1' }}>
                  Branding (optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1', mt: -0.5 }}>
                  Hover a color field or click the swatch to open presets; the hex code is saved.
                </Typography>
                <TenantBrandingColorField
                  name="primaryColor"
                  label="Primary color"
                  placeholder="#1976d2"
                  value={values.primaryColor}
                  setFieldValue={setFieldValue}
                  onBlur={handleBlur}
                  error={touched.primaryColor && errors.primaryColor}
                  helperText={
                    (touched.primaryColor && errors.primaryColor) || 'Preset or custom (#RRGGBB)'
                  }
                />
                <TenantBrandingColorField
                  name="secondaryColor"
                  label="Secondary color"
                  placeholder="#ffffff"
                  value={values.secondaryColor}
                  setFieldValue={setFieldValue}
                  onBlur={handleBlur}
                  error={touched.secondaryColor && errors.secondaryColor}
                  helperText={touched.secondaryColor && errors.secondaryColor}
                />
                <TenantBrandingColorField
                  name="backgroundColor"
                  label="Background color"
                  placeholder="#f5f5f5"
                  value={values.backgroundColor}
                  setFieldValue={setFieldValue}
                  onBlur={handleBlur}
                  error={touched.backgroundColor && errors.backgroundColor}
                  helperText={touched.backgroundColor && errors.backgroundColor}
                />
                <TenantLogoUploadField
                  value={values.tenantPhoto}
                  setFieldValue={setFieldValue}
                  registryTenantId={tenant.id}
                  disabled={submitting}
                  error={touched.tenantPhoto && errors.tenantPhoto}
                  helperText="After upload, click Save to persist the path. Remove logo clears it on save."
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditTenantDialog;
